import {
  Button,
  Checkbox,
  FormField,
  Input,
  Modal,
  SpaceBetween
} from "@cloudscape-design/components";
import { useEffect, useState } from "react";
import { InstallationParametersModalProps } from "types/extensionTypes";





const InstallationParametersModal = ({
  visible,
  extension,
  onDismiss,
  onConfirm
}: InstallationParametersModalProps) => {

  const [parameters, setParameters] = useState<Record<string, any>>({});



  const isFormValid = () => {
    if (!extension?.parametersSchema) return true;

    return extension.parametersSchema.every((parameter) => {
      const value = parameters[parameter.name];

      // Optional parameter can be empty
      if (!parameter.required && value === "") {
        return true;
      }

      if (parameter.type === "string") {
        return typeof value === "string" && value.trim() !== "";
      }

      if (parameter.type === "number") {
        return value !== "" && !isNaN(Number(value));
      }

      // Boolean parameters are always valid
      if (parameter.type === "boolean") {
        return true;
      }

      return true;
    });
  };



  useEffect(() => {
    if (!extension) return;

    const initialParameters: Record<string, any> = {};

    extension.parametersSchema?.forEach((parameter) => {
      if (parameter.type === "boolean") {
        initialParameters[parameter.name] = parameter.default ?? false;
      } else {
        initialParameters[parameter.name] = "";
      }
    });

    setParameters(initialParameters);
  }, [extension]);



  const handleClose = () => {
    setParameters({});
    onDismiss();
  };



  const handleConfirm = async () => {
    // Convert numeric strings to actual JavaScript numbers before sending
    const formattedParams: Record<string, any> = { ...parameters };

    extension?.parametersSchema?.forEach((param) => {
      const value = formattedParams[param.name];

      // Optional field left empty → use its default value
      if (!param.required && value === "") {
        formattedParams[param.name] = param.default;
        return;
      }

      // Convert numbers to actual numbers
      if (param.type === "number" && value !== "") {
        formattedParams[param.name] = Number(value);
      }
    });

    // Send parameters to parent but keep this modal open so the user can still edit or see values while the confirmation modal is shown.
    // Do NOT clear internal state or dismiss the modal here.
    await onConfirm(formattedParams);
  };



  return (
    <Modal
      visible={visible}
      onDismiss={handleClose}
      header={`Install ${extension?.displayName}`}
      closeAriaLabel="Close modal"
      footer={
        <SpaceBetween direction="horizontal" size="xs">
          <Button variant="link" onClick={handleClose}>
            Cancel
          </Button>

          <Button
            variant="primary"
            disabled={!isFormValid()}
            onClick={handleConfirm}
          >
            Install
          </Button>
        </SpaceBetween>
      }
    >
      <SpaceBetween size="l">
        {extension?.parametersSchema?.length === 0 ? (
          <div>No installation parameters required.</div>
        ) : (
          extension?.parametersSchema?.map((parameter) => (
            <FormField
              key={parameter.name}
              label={
                parameter.required ? (
                  <>
                    {parameter.label} <span className="required-field">*</span>
                  </>
                ) : (
                  parameter.label
                )
              }
            >
              {parameter.type === "boolean" ? (
                <Checkbox
                  checked={parameters[parameter.name] || false}
                  onChange={({ detail }) =>
                    setParameters({
                      ...parameters,
                      [parameter.name]: detail.checked
                    })
                  }
                />
              ) : parameter.type === "number" ? (
                <Input
                  type="number"
                  value={String(parameters[parameter.name] ?? "")}
                  placeholder={parameter.required ? "Enter numeric value" : `Default: ${parameter.default}`}
                  onChange={({ detail }) =>
                    setParameters({
                      ...parameters,
                      [parameter.name]: detail.value
                    })
                  }
                />
              ) : (
                <Input
                  value={parameters[parameter.name] || ""}
                  placeholder={parameter.required ? "Enter parameter value" : `Default: ${parameter.default}`}
                  onChange={({ detail }) =>
                    setParameters({
                      ...parameters,
                      [parameter.name]: detail.value
                    })
                  }
                />
              )}
            </FormField>
          ))
        )}
      </SpaceBetween>
    </Modal>
  );
};

export default InstallationParametersModal;